import { createContext, ReactNode, useContext, useMemo } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { refundHistoryQuery } from '@/__generated__/refundHistoryQuery.graphql';
import { refundHistory } from '@/graphql/query/tax-refund/refund-history';

export interface RefundItemModel {
  id: string;
  corpType: string;
  bank: string;
  accountNo: string;
  refundAmount: string;
  paymentAmount: number;
  bizName?: string;
  bizNo?: string;
  refundResultDt: string | null | undefined;
  paymentDt: string | null | undefined;
  applicationDt: string;
}

interface RefundItemContextProviderProps {
  children: ReactNode;
  queryRef: PreloadedQuery<refundHistoryQuery, Record<string, unknown>>;
}

interface RefundItemContextModel {
  userName: string;
  refundItems: RefundItemModel[];
}

const RefundItemContext = createContext({} as RefundItemContextModel);
export const useRefundItemContext = () => useContext<RefundItemContextModel>(RefundItemContext);

export const RefundItemContextProvider = ({ children, queryRef }: RefundItemContextProviderProps) => {
  const { bznavRefund } = usePreloadedQuery<refundHistoryQuery>(refundHistory, queryRef);

  const refundItems: RefundItemModel[] = useMemo(
    () =>
      bznavRefund.user.refundDetail?.map((item) => {
        const corpType = item.corpType === 'CORPS' ? 'CORPS' : 'INDIS';
        const isCorpType = item.corpType === 'CORPS';
        return {
          id: item.id,
          corpType,
          bank: item.applicationInfo?.bank || '',
          accountNo: item.applicationInfo?.accountNo || '',
          refundAmount: item.refundAmount || '',
          paymentAmount: item.paymentAmount || 0,
          bizName: isCorpType ? item.businessInfo?.hometaxBizName || '' : item.businessInfo?.taxpayerName || '',
          bizNo: isCorpType ? item.businessInfo?.hometaxBizNo || '' : '',
          refundResultDt: item.refundResultDt,
          paymentDt: item.paymentDate,
          applicationDt: item.applicationDt || ''
        };
      }) || [],
    [bznavRefund.user.refundDetail]
  );

  const initValue = useMemo(
    () => ({
      userName: bznavRefund.user.name || '',
      refundItems
    }),
    [bznavRefund.user.name, refundItems]
  );

  return <RefundItemContext.Provider value={initValue}>{children}</RefundItemContext.Provider>;
};
